import { useForm } from '@mantine/form';
import { IconBuildingSkyscraper, IconCheck, IconPlus } from '@tabler/icons-react';
import { isEmpty, noop } from 'lodash';
import { FormProps } from 'components/forms/FormProps';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { useConfirm } from 'components/modals/message/MessageProvider';
import useLoadingAction from 'hooks/use-loading-action';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FormInputGroup from 'components/forms/FormInputGroup';
import { Box, Checkbox, Group, MultiSelect, SimpleGrid, TextInput } from '@mantine/core';
import FormWrapper from 'components/forms/FormWrapper';
import ThreeWaySelect, { SelectedEntityRow } from 'components/forms/device-subtype/ThreeWaySelect';
import P1Regular from 'components/typography/P1Regular';
import { Stack } from '@mantine/core';
import P2Regular from 'components/typography/P2Regular';
import P1Medium from 'components/typography/P1Medium';
import PickOrganizations from 'components/inputs/multi-checkbox/PickOrganizations';
import slugify from 'slugify';
import { DeviceTypeSelect } from 'components/selects/DeviceTypeSelect';
import { FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR } from 'env';
import { nanoid } from 'nanoid';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';
import Toast from 'components/Toast';
import { KIND_OPTIONS_WITHOUT_MANUAL } from 'components/tables/data/kind';
import { canManageTemplatesGlobally } from 'components/forms/revision-template/revision-template-helpers';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';

export interface RevisionTemplateFormData {
  name: string;
  kinds: string[];
  restriction: 'global' | 'organization';
  organizations: { organizationId: number; organizationName: string }[];
  blacklistedOrganizations?: { organizationId: number; organizationName: string }[];
  slug: string;
  deviceTypeId: string;
  supportsKep?: boolean;
  singleDeviceOnly?: boolean;
}

export const revisionTemplateInitialValues = {
  name: '',
  kinds: [],
  restriction: 'global' as const,
  organizations: [],
  blacklistedOrganizations: [],
  slug: '',
  deviceTypeId: '',
  supportsKep: true,
  singleDeviceOnly: false,
};

export interface RevisionTemplateFormProps extends FormProps<RevisionTemplateFormData> {
  readOnly?: Partial<Record<keyof RevisionTemplateFormData, boolean>>;
  primaryButtonText?: string;
  primaryIcon?: React.ReactNode;
  metadataReadOnly?: boolean;
  onSaved?: () => void;
}

/**
 * Form for editing a revision template.
 */
export default function RevisionTemplateForm({
  initialValues = revisionTemplateInitialValues,
  onSubmit = noop,
  readOnly = {},
  primaryButtonText = 'Uložiť',
  primaryIcon = <IconCheck stroke="1.5" size={24} />,
  metadataReadOnly = false,
  onSaved = noop,
}: RevisionTemplateFormProps) {
  const { roleId, getAction, hasPermissionGlobally } = useApi();
  const userCanManageTemplatesGlobally = canManageTemplatesGlobally(roleId, hasPermissionGlobally('manage-templates'));
  const [singleAllowedOrganization, setSingleAllowedOrganization] = useState<{
    organizationId: number;
    organizationName: string;
  } | null>(null);
  const { confirm } = useConfirm();
  const [openedOrganizations, { close: closeOrganizations, open: openOrganizations }] = useDisclosure(false);
  const [organizationField, setOrganizationField] = useState<'organizations' | 'blacklistedOrganizations'>(
    'organizations'
  );
  const { isTablet } = useResponsiveDesign();

  const form = useForm({
    initialValues: { ...initialValues, blacklistedOrganizations: initialValues.blacklistedOrganizations ?? [] },
    validate: {
      name: createValidator([required]),
      organizations: (value) => {
        if (form.values.restriction === 'organization' && isEmpty(value)) {
          return 'Vyberte aspoň jednu organizáciu';
        }

        return null;
      },
    },
  });
  useDirtyFormHandler(form);

  const [{ loading }, submit] = useLoadingAction(async (values: typeof form.values) => {
    try {
      await onSubmit(values);
      form.resetDirty(values);
      onSaved();
    } catch (error) {
      panic(error as Error);
    }
  });

  /**
   * Confirms the removal of the organization from the list of selected organizations.
   */
  const confirmRemoveOrganization = useCallback(
    (organizationName: string, index: number, field = 'organizations') => {
      confirm({
        title: 'Odstrániť organizáciu',
        content: `Naozaj chcete odstrániť organizáciu ${organizationName} zo zoznamu?`,
        onConfirm: () => form.removeListItem(field, index),
      });
    },
    [confirm]
  );

  const addOrganizationButton = useMemo(
    () => (
      <Box>
        <Button
          onClick={() => {
            setOrganizationField('organizations');
            openOrganizations();
          }}
          variant="subtle"
          leftIcon={<IconPlus />}
        >
          Pridať organizácie
        </Button>
      </Box>
    ),
    [openOrganizations]
  );

  useEffect(() => {
    if (!metadataReadOnly && form.values.name !== '') {
      const slug = slugify(form.values.name, { lower: true });
      const slugWithId = `${slug}-${nanoid()}`;

      form.setFieldValue('slug', slugWithId);
    }
  }, [form.values.name, metadataReadOnly]);

  useEffect(() => {
    if (!metadataReadOnly && !userCanManageTemplatesGlobally && form.values.restriction === 'global') {
      form.setFieldValue('restriction', 'organization');
    }
  }, [userCanManageTemplatesGlobally, form.values.restriction, metadataReadOnly]);

  useEffect(() => {
    if (metadataReadOnly || userCanManageTemplatesGlobally) {
      setSingleAllowedOrganization(null);
      return;
    }

    const getOrganizationsWithPermission = getAction('AuthGetOrganizationsWithPermission');

    getOrganizationsWithPermission({ parameters: { permissionSlug: 'manage-templates' } })
      .then((organizations) => {
        if (organizations.length === 1) {
          setSingleAllowedOrganization(organizations[0]);
        } else {
          setSingleAllowedOrganization(null);
        }
      })
      .catch(panic);
  }, [userCanManageTemplatesGlobally, getAction, metadataReadOnly]);

  useEffect(() => {
    if (metadataReadOnly || !singleAllowedOrganization) {
      return;
    }

    const alreadySelected =
      form.values.restriction === 'organization' &&
      form.values.organizations.length === 1 &&
      form.values.organizations[0].organizationId === singleAllowedOrganization.organizationId;

    if (alreadySelected) {
      return;
    }

    form.setValues({
      ...form.values,
      restriction: 'organization',
      organizations: [singleAllowedOrganization],
    });
  }, [singleAllowedOrganization, metadataReadOnly]);

  return (
    <FormWrapper
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText={primaryButtonText}
      primaryIcon={primaryIcon}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <Box component="fieldset" disabled={loading} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <FormInputGroup groupTitle="Základné informácie">
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <TextInput
              label="Názov šablóny"
              placeholder="Názov šablóny"
              size="lg"
              name="objekto-name"
              disabled={metadataReadOnly || readOnly.name}
              {...form.getInputProps('name')}
            />
            <Stack spacing={8}>
              <MultiSelect
                label="Typy šablóny"
                placeholder="Vyberte typy šablóny"
                size="lg"
                name="objekto-kinds"
                data={KIND_OPTIONS_WITHOUT_MANUAL}
                searchable
                clearable
                disabled={metadataReadOnly || readOnly.kinds}
                readOnly={metadataReadOnly || readOnly.kinds}
                {...form.getInputProps('kinds')}
              />
              {form.values.kinds.length === 0 && (
                <Toast
                  type="warning"
                  size="s"
                  withCloseButton={false}
                  message="Šablóna bez typu bude skrytá vo výbere šablón pre revízie a formuláre."
                />
              )}
            </Stack>
            {FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR && (
              <DeviceTypeSelect
                label="Zariadenie"
                placeholder="Vyberte zariadenie"
                size="lg"
                name="objekto-device-type"
                disabled={metadataReadOnly || readOnly.deviceTypeId}
                readOnly={metadataReadOnly || readOnly.deviceTypeId}
                {...form.getInputProps('deviceTypeId')}
              />
            )}
          </SimpleGrid>
        </FormInputGroup>

        <FormInputGroup groupTitle="KEP podpora">
          <Checkbox
            label="Šablóna podporuje KEP"
            disabled={metadataReadOnly || readOnly.supportsKep}
            size="md"
            {...form.getInputProps('supportsKep', { type: 'checkbox' })}
          />
        </FormInputGroup>

        <FormInputGroup groupTitle="Určenie šablóny">
          <Checkbox
            label="Šablóna iba pre jedno zariadenie"
            disabled={metadataReadOnly || readOnly.singleDeviceOnly}
            size="md"
            {...form.getInputProps('singleDeviceOnly', { type: 'checkbox' })}
          />
        </FormInputGroup>

        <FormInputGroup groupTitle="Vymedzenie pre:">
          {metadataReadOnly ? (
            <P1Regular color="gray.8">Globálna šablóna</P1Regular>
          ) : singleAllowedOrganization ? (
            <Group spacing={8} align="center" noWrap>
              <IconBuildingSkyscraper stroke="1.5" />
              <P1Regular color="gray.8" mt={4}>
                {singleAllowedOrganization.organizationName}
              </P1Regular>
              <QuestionMarkTooltip label="Šablóna bude dostupná iba pre vašu organizáciu" />
            </Group>
          ) : (
            <ThreeWaySelect
              {...form.getInputProps('restriction')}
              hideDepartment
              permissionSlug="manage-templates"
              forceAllowGlobal={userCanManageTemplatesGlobally}
            />
          )}
          {form.values.restriction === 'global' && (
            <P1Regular color="gray.6">
              Šablóna bude dostupná pre všetky organizácie okrem vylúčených organizácií.
            </P1Regular>
          )}
          {form.values.restriction === 'organization' &&
            !singleAllowedOrganization &&
            (isEmpty(form.values.organizations) ? (
              <Stack align="flex-start">
                <P1Regular color="gray.6">
                  Zatiaľ nie sú pridané žiadne organizácie. Táto šablóna bude dostupná len pre vybrané organizácie.
                </P1Regular>
                <P2Regular color="red.8">{form.errors.organizations}</P2Regular>
                {addOrganizationButton}
              </Stack>
            ) : (
              <Stack spacing={24}>
                <Box>
                  <Box bg="gray.0" style={{ borderRadius: '4px' }} p={16}>
                    <P1Medium color="gray.8">Organizácia</P1Medium>
                  </Box>

                  {form.values.organizations.map((organization, index) => (
                    <SelectedEntityRow
                      key={organization.organizationId}
                      name={organization.organizationName}
                      deleteText="Zmazať organizáciu"
                      onDelete={() => {
                        confirmRemoveOrganization(organization.organizationName, index);
                      }}
                    />
                  ))}
                </Box>

                {addOrganizationButton}
              </Stack>
            ))}
        </FormInputGroup>

        {form.values.restriction === 'global' && (
          <FormInputGroup groupTitle="Nedostupná pre organizácie">
            <Stack spacing={24}>
              {!userCanManageTemplatesGlobally && (
                <P1Regular color="gray.6">Zobrazené sú iba organizácie, ktorých šablóny môžete spravovať.</P1Regular>
              )}
              {isEmpty(form.values.blacklistedOrganizations) ? (
                <P1Regular color="gray.6">Zatiaľ nie sú vylúčené žiadne organizácie.</P1Regular>
              ) : (
                <Box>
                  <Box bg="gray.0" style={{ borderRadius: '4px' }} p={16}>
                    <P1Medium color="gray.8">Organizácia</P1Medium>
                  </Box>
                  {form.values.blacklistedOrganizations.map((organization, index) => (
                    <SelectedEntityRow
                      key={organization.organizationId}
                      name={organization.organizationName}
                      deleteText="Zrušiť vylúčenie"
                      onDelete={() =>
                        confirmRemoveOrganization(organization.organizationName, index, 'blacklistedOrganizations')
                      }
                    />
                  ))}
                </Box>
              )}
              <Box>
                <Button
                  onClick={() => {
                    setOrganizationField('blacklistedOrganizations');
                    openOrganizations();
                  }}
                  variant="subtle"
                  leftIcon={<IconPlus />}
                >
                  Pridať organizácie
                </Button>
              </Box>
            </Stack>
          </FormInputGroup>
        )}

        <PickOrganizations
          opened={openedOrganizations}
          onClose={closeOrganizations}
          onPick={(organizations) => {
            for (const item of organizations) {
              form.insertListItem(organizationField, item);
            }
          }}
          excludeIds={form.values[organizationField].map(({ organizationId }) => organizationId)}
          permissionSlug="manage-templates"
        />
      </Box>
    </FormWrapper>
  );
}
